import { useEffect, useMemo, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, Minus, Plus, ShieldCheck, ShoppingBag, Truck, X } from "lucide-react";
import { http, fmtErr } from "../lib/api";
import { useRetailCart } from "../contexts/RetailCartContext";
import { useConfig } from "../contexts/ConfigContext";
import { ProductImage } from "../components/ProductImage";
import { WaitlistForm } from "../components/WaitlistForm";

export default function RetailCart() {
  const { items, setQty, remove, clear } = useRetailCart();
  const { purchases_enabled } = useConfig();
  const [products, setProducts] = useState([]);
  const [checkingOut, setCheckingOut] = useState(false);
  const [customer, setCustomer] = useState({ name: "", email: "" });
  const [preview, setPreview] = useState({ total: 0, promo: { jars_paid: 0, free_mix_jars: 0, next_bonus_at: 3 } });

  useEffect(() => { http.get("/products").then(({ data }) => setProducts(data.products)); }, []);

  useEffect(() => {
    if (items.length === 0) { setPreview({ total: 0, promo: { jars_paid: 0, free_mix_jars: 0, next_bonus_at: 3 } }); return; }
    http.post("/retail/preview", {
      items: items.map((i) => ({ product_id: i.product_id, quantity: i.quantity })),
      origin_url: window.location.origin,
    }).then(({ data }) => setPreview(data)).catch(() => {});
  }, [items]);

  const productMap = useMemo(() => Object.fromEntries(products.map((p) => [p.id, p])), [products]);
  const cartLines = items.map((i) => ({ ...i, product: productMap[i.product_id] })).filter((l) => l.product);
  const total = preview.total;
  const jarsTotal = preview.promo.jars_paid;
  const freeJars = preview.promo.free_mix_jars;
  const jarsToNextBonus = preview.promo.next_bonus_at - jarsTotal;
  const promoProgress = jarsTotal === 0 ? 0 : ((jarsTotal % 3) / 3) * 100;
  const promoUnlocked = freeJars > 0;

  const FREE_SHIPPING = 50;
  const shipProgress = Math.min(100, (total / FREE_SHIPPING) * 100);
  const toFreeShip = Math.max(0, FREE_SHIPPING - total);

  const checkout = async () => {
    setCheckingOut(true);
    try {
      const { data } = await http.post("/retail/checkout", {
        items: items.map((i) => ({ product_id: i.product_id, quantity: i.quantity })),
        origin_url: window.location.origin,
        email: customer.email || null,
        shipping_name: customer.name || null,
      });
      window.location.href = data.url;
    } catch (err) {
      toast.error(fmtErr(err.response?.data?.detail));
      setCheckingOut(false);
    }
  };

  return (
    <div className="bg-[#FFFDF8]" data-testid="retail-cart-root">
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-12">
        <div className="text-xs tracking-[0.3em] uppercase text-[#D32F2F] mb-3">Your bag</div>
        <h1 className="font-heading text-6xl uppercase tracking-wide">Retail checkout</h1>

        <div className="grid md:grid-cols-3 gap-10 mt-10">
          <div className="md:col-span-2">
            {cartLines.length > 0 && (
              <div className={`mb-6 p-5 border-2 ${promoUnlocked ? "border-[#7CB342] bg-[#7CB342]/10" : "border-[#D32F2F] bg-[#D32F2F]/5"}`} data-testid="cart-promo-tracker">
                <div className="flex items-center gap-3 mb-3">
                  <div className="text-3xl">🎁</div>
                  <div className="flex-1">
                    {promoUnlocked ? (
                      <div>
                        <div className="font-heading text-lg uppercase tracking-wide text-[#7CB342]">You unlocked {freeJars} FREE mixed chamoy {freeJars > 1 ? "surprises" : "surprise"}!</div>
                        <div className="text-xs text-[#5C5A56] mt-1">Add {jarsToNextBonus} more chilito{jarsToNextBonus > 1 ? "s" : ""} for another freebie</div>
                      </div>
                    ) : (
                      <div>
                        <div className="font-heading text-lg uppercase tracking-wide text-[#D32F2F]">Add {jarsToNextBonus} more chilito{jarsToNextBonus > 1 ? "s" : ""} to unlock a FREE mixed chamoy surprise</div>
                        <div className="text-xs text-[#5C5A56] mt-1">Buy 3, get 1 free · every 3 chilito candies</div>
                      </div>
                    )}
                  </div>
                </div>
                <div className="w-full bg-white border border-[#E5D8C1] h-3 overflow-hidden">
                  <div className={`h-full transition-all ${promoUnlocked ? "bg-[#7CB342]" : "bg-[#D32F2F]"}`} style={{ width: `${promoUnlocked ? 100 : promoProgress}%` }} />
                </div>
              </div>
            )}

            {cartLines.length === 0 ? (
              <div className="border border-dashed border-[#E5D8C1] p-12 text-center text-[#5C5A56]" data-testid="retail-empty">
                <ShoppingBag size={40} className="mx-auto mb-3 opacity-40" />
                Your bag is empty. <Link to="/shop" className="text-[#D32F2F] font-semibold">Browse the treats →</Link>
              </div>
            ) : (
              <div className="space-y-3">
                {cartLines.map((l) => (
                  <div key={l.product_id} className="bg-[#FDF4E3] border border-[#E5D8C1] p-4 flex items-center gap-4" data-testid={`retail-line-${l.product_id}`}>
                    <div className="w-20 h-20 shrink-0 relative overflow-hidden">
                      <ProductImage p={l.product} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-heading text-lg uppercase leading-tight">{l.product.name}</div>
                      <div className="text-sm text-[#5C5A56]">${l.product.retail_msrp.toFixed(2)} · {l.product.size_oz} oz chilito candy</div>
                    </div>
                    <div className="flex items-center border-2 border-[#1A1A1A]">
                      <button onClick={() => setQty(l.product_id, l.quantity - 1)} className="w-9 h-9 flex items-center justify-center hover:bg-[#1A1A1A] hover:text-white" data-testid={`retail-minus-${l.product_id}`}><Minus size={14} /></button>
                      <div className="w-10 text-center text-sm font-semibold">{l.quantity}</div>
                      <button onClick={() => setQty(l.product_id, l.quantity + 1)} className="w-9 h-9 flex items-center justify-center hover:bg-[#1A1A1A] hover:text-white" data-testid={`retail-plus-${l.product_id}`}><Plus size={14} /></button>
                    </div>
                    <div className="w-24 text-right font-heading text-xl">${(l.product.retail_msrp * l.quantity).toFixed(2)}</div>
                    <button onClick={() => remove(l.product_id)} className="text-[#5C5A56] hover:text-[#D32F2F]" data-testid={`retail-remove-${l.product_id}`}><X size={18} /></button>
                  </div>
                ))}
                {freeJars > 0 && (
                  <div className="bg-[#7CB342]/15 border-2 border-dashed border-[#7CB342] p-4 flex items-center gap-4" data-testid="retail-bonus-line">
                    <div className="w-20 h-20 shrink-0 bg-gradient-to-br from-[#FFB300] via-[#D32F2F] to-[#7CB342] flex items-center justify-center">
                      <div className="text-4xl">🎁</div>
                    </div>
                    <div className="flex-1">
                      <div className="font-heading text-lg uppercase leading-tight">Aleka&apos;s Mixed Chamoy Surprise</div>
                      <div className="text-sm text-[#5C5A56]">Bonus — Aleka picks a candy to send with your order</div>
                    </div>
                    <div className="text-sm font-heading uppercase tracking-widest text-[#7CB342]">×{freeJars}</div>
                    <div className="w-24 text-right font-heading text-xl text-[#7CB342]">FREE</div>
                  </div>
                )}
              </div>
            )}

            {cartLines.length > 0 && (
              <div className="mt-8 bg-[#FDF4E3] border border-[#E5D8C1] p-6">
                <div className="font-heading text-xl uppercase tracking-wide mb-4">Optional: send us your details</div>
                <div className="grid md:grid-cols-2 gap-4">
                  <label className="block"><span className="text-xs uppercase tracking-widest">Name</span>
                    <input value={customer.name} onChange={(e) => setCustomer({...customer, name: e.target.value})} className="w-full mt-2 bg-white border border-[#E5D8C1] px-4 py-3 focus:outline-none focus:border-[#D32F2F]" data-testid="retail-name" />
                  </label>
                  <label className="block"><span className="text-xs uppercase tracking-widest">Email</span>
                    <input type="email" value={customer.email} onChange={(e) => setCustomer({...customer, email: e.target.value})} className="w-full mt-2 bg-white border border-[#E5D8C1] px-4 py-3 focus:outline-none focus:border-[#D32F2F]" data-testid="retail-email" />
                  </label>
                </div>
                <div className="text-xs text-[#5C5A56] mt-3">Stripe will collect your shipping address at checkout.</div>
              </div>
            )}
          </div>

          <div>
            <div className="bg-[#1A1A1A] text-[#FFFDF8] p-8 sticky top-24">
              <h3 className="font-heading text-2xl uppercase tracking-wide">Bag total</h3>

              {cartLines.length > 0 && (
                <div className="mt-4 mb-6">
                  {total >= FREE_SHIPPING ? (
                    <div className="text-[#7CB342] text-xs uppercase tracking-widest flex items-center gap-2">
                      <Truck size={14} /> You&apos;ve unlocked free shipping!
                    </div>
                  ) : (
                    <>
                      <div className="text-xs text-white/60 uppercase tracking-widest">${toFreeShip.toFixed(2)} to free shipping</div>
                      <div className="w-full bg-white/10 h-2 mt-1 overflow-hidden">
                        <div className="h-full bg-[#FFB300] transition-all" style={{ width: `${shipProgress}%` }} />
                      </div>
                    </>
                  )}
                </div>
              )}

              <div className="flex justify-between pb-4 border-b border-white/20">
                <span className="text-sm uppercase tracking-widest text-white/70">Chilitos</span>
                <span>{jarsTotal}{freeJars > 0 && <span className="text-[#7CB342] text-xs ml-2">+{freeJars} free</span>}</span>
              </div>
              <div className="flex justify-between mt-4">
                <span className="text-sm uppercase tracking-widest text-white/70">Subtotal</span>
                <span className="font-heading text-3xl text-[#FFB300]">${total.toFixed(2)}</span>
              </div>
              <div className="text-xs text-white/50 mt-2">Shipping &amp; tax calculated at checkout.</div>
              {purchases_enabled ? (
                <>
                  <button
                    disabled={cartLines.length === 0 || checkingOut}
                    onClick={checkout}
                    className="mt-6 w-full bg-[#D32F2F] hover:bg-[#B71C1C] py-4 uppercase tracking-widest font-heading text-lg disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center gap-2 transition-transform hover:-translate-y-1"
                    data-testid="retail-checkout"
                  >
                    {checkingOut && <Loader2 className="animate-spin" size={18} />}
                    Checkout — ${total.toFixed(2)}
                  </button>
                  <div className="mt-3 text-center text-[10px] text-white/40 uppercase tracking-widest">Secure Stripe checkout · Apple Pay · Cards</div>
                </>
              ) : (
                <div className="mt-6" data-testid="retail-checkout-disabled">
                  <div className="bg-[#FFB300] text-[#1A1A1A] text-center p-4 font-heading uppercase tracking-widest text-sm">🚀 Online orders launching soon</div>
                  <div className="mt-4">
                    <WaitlistForm interest="retail" compact headline="Get notified at launch" />
                  </div>
                </div>
              )}
              {cartLines.length > 0 && (
                <button onClick={clear} className="mt-3 w-full text-xs uppercase tracking-widest text-white/50 hover:text-white" data-testid="retail-clear">Clear bag</button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export function RetailSuccess() {
  const [status, setStatus] = useState("checking");
  const [details, setDetails] = useState(null);
  const { clear } = useRetailCart();
  const location = useLocation();
  const sessionId = new URLSearchParams(location.search).get("session_id");
  useEffect(() => {
    if (!sessionId) { setStatus("error"); return; }
    let attempts = 0;
    const poll = async () => {
      try {
        const { data } = await http.get(`/checkout/status/${sessionId}`);
        setDetails(data);
        if (data.payment_status === "paid") { setStatus("paid"); clear(); return; }
        if (data.status === "expired") { setStatus("expired"); return; }
      } catch (e) {
        console.warn("retail status poll failed", e?.message || e);
      }
      attempts++;
      if (attempts < 6) setTimeout(poll, 2000);
      else setStatus("timeout");
    };
    poll();
  }, [sessionId]);

  return (
    <div className="max-w-2xl mx-auto px-6 py-24 text-center" data-testid="retail-success-root">
      {status === "checking" && (<>
        <Loader2 className="animate-spin mx-auto mb-4" size={40} />
        <h1 className="font-heading text-4xl uppercase tracking-wide">Confirming your payment…</h1>
      </>)}
      {status === "paid" && (<>
        <div className="w-20 h-20 mx-auto bg-[#7CB342] flex items-center justify-center mb-6"><ShieldCheck className="text-white" size={40} /></div>
        <h1 className="font-heading text-5xl uppercase tracking-wide">Thank you!</h1>
        <p className="text-[#5C5A56] mt-3">
          Your order{details?.amount_total ? ` of $${(details.amount_total / 100).toFixed(2)}` : ""} is confirmed. Your chilito candies will ship within 2 business days — tracking to follow by email.
        </p>
        <div className="mt-8 flex flex-wrap gap-3 justify-center">
          <Link to="/shop" className="border-2 border-[#1A1A1A] px-8 py-3 uppercase tracking-widest font-heading hover:bg-[#1A1A1A] hover:text-white">Keep shopping</Link>
          <Link to="/orders" className="border-2 border-[#D32F2F] text-[#D32F2F] px-8 py-3 uppercase tracking-widest font-heading hover:bg-[#D32F2F] hover:text-white" data-testid="retail-success-track">Track this order</Link>
        </div>
      </>)}
      {(status === "expired" || status === "error" || status === "timeout") && (<>
        <h1 className="font-heading text-4xl uppercase tracking-wide">Payment {status}</h1>
        <p className="text-[#5C5A56] mt-3">If you were charged and don&apos;t see confirmation, email us with session id <code className="bg-[#FDF4E3] px-2 py-1">{sessionId || "unknown"}</code>.</p>
        <Link to="/shop" className="inline-block mt-8 border-2 border-[#1A1A1A] px-8 py-3 uppercase tracking-widest font-heading hover:bg-[#1A1A1A] hover:text-white">Back to shop</Link>
      </>)}
    </div>
  );
}
